/**
 * Comprobante adicional que sube una persona (Contaduría en el Paso 1,
 * Aprobador en el Paso 2). [NUEVO 08/09/2026]
 *
 * El archivo viaja como **base64 dentro del JSON**, no como
 * `multipart/form-data`. No es preferencia: el proxy same-origin de la Static
 * Web App (`api/src/functions/proxy.js`) reenvía todo body no-GET leyéndolo
 * como TEXTO (`request.text()`, UTF-8), así que un binario se corrompería en
 * el camino — y ese proxy no se puede saltear (existe por el problema de Easy
 * Auth con el preflight CORS, ver docs/Decisions.md). Base64 es ASCII y pasa
 * intacto, y de paso el request sigue usando el mismo helper `request<T>()`
 * que todo el resto, que fuerza `Content-Type: application/json`.
 */

/** Igual que ADJUNTO_MAX_BYTES del backend (shared/blob_storage.py). */
export const ADJUNTO_MAX_BYTES = 10 * 1024 * 1024;

const TIPOS_PERMITIDOS = ["application/pdf", "image/jpeg", "image/png"];
const EXTENSIONES_PERMITIDAS = [".pdf", ".jpg", ".jpeg", ".png"];

/**
 * Control BLANDO, del lado del cliente: sirve para no invitar a un POST que el
 * backend va a rechazar, y para no subir 40 MB al vacío. **La validación real
 * vive en el backend**, que decide el tipo por los primeros bytes del
 * contenido (magic bytes) y no por lo que diga el browser.
 *
 * Devuelve `null` si está bien, o el motivo para mostrar si no.
 *
 * Es deliberadamente tolerante con el tipo: `file.type` puede venir vacío
 * según el sistema operativo, así que si no hay tipo se cae a la extensión, y
 * si tampoco hay extensión se deja pasar y decide el backend. Con el tamaño,
 * en cambio, es estricto — ahí sí conviene cortar antes de subir.
 */
export function motivoRechazoAdjunto(file: File): string | null {
  if (file.size === 0) return "El archivo está vacío.";
  if (file.size > ADJUNTO_MAX_BYTES) {
    return `El archivo pesa ${(file.size / (1024 * 1024)).toFixed(1)} MB y el máximo es 10 MB.`;
  }
  if (file.type) {
    if (!TIPOS_PERMITIDOS.includes(file.type)) return "Solo se aceptan archivos PDF, JPG o PNG.";
    return null;
  }
  const nombre = file.name.toLowerCase();
  const tieneExtensionConocida = EXTENSIONES_PERMITIDAS.some((ext) => nombre.endsWith(ext));
  const tieneAlgunaExtension = /\.[a-z0-9]+$/.test(nombre);
  if (tieneAlgunaExtension && !tieneExtensionConocida) {
    return "Solo se aceptan archivos PDF, JPG o PNG.";
  }
  return null;
}

/**
 * Recorta el prefijo `data:<mime>;base64,` que agrega
 * `FileReader.readAsDataURL`, y devuelve solo el base64 — que es lo que espera
 * el backend en `adjunto_base64`.
 *
 * Exportada aparte de la lectura del archivo justamente para poder testearla
 * sin un `File` ni un `FileReader` de verdad.
 */
export function recortarPrefijoDataUrl(dataUrl: string): string {
  const coma = dataUrl.indexOf(",");
  if (dataUrl.startsWith("data:") && coma !== -1) return dataUrl.slice(coma + 1);
  return dataUrl;
}

/** Lee el archivo y lo devuelve como base64 puro (sin el prefijo data:). */
export function leerArchivoComoBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = () => resolve(recortarPrefijoDataUrl(String(lector.result ?? "")));
    lector.onerror = () => reject(new Error("No se pudo leer el archivo."));
    lector.readAsDataURL(file);
  });
}
